import { useState, useEffect, useCallback } from 'react'

export interface DemoPurchaseItem {
  id: number | string
  title: string
  price: number
  image: string
  quantity: number
}

export interface DemoPurchase {
  orderId: string
  items: DemoPurchaseItem[]
  total: number
  date: string
}

const STORAGE_KEY = 'dgstack_demo_purchases'

function loadPurchases(): DemoPurchase[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {
    // ignore parse errors
  }
  return []
}

function savePurchases(purchases: DemoPurchase[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(purchases))
}

export function useDemoPurchases() {
  const [purchases, setPurchases] = useState<DemoPurchase[]>(loadPurchases)

  // Sync from localStorage on mount (handles cross-tab or navigation)
  useEffect(() => {
    setPurchases(loadPurchases())
  }, [])

  const addPurchase = useCallback(
    (items: DemoPurchaseItem[], total: number) => {
      const newPurchase: DemoPurchase = {
        orderId: `ORD-${Date.now().toString(36).toUpperCase()}`,
        items,
        total,
        date: new Date().toISOString(),
      }
      const updated = [newPurchase, ...loadPurchases()]
      savePurchases(updated)
      setPurchases(updated)
      return newPurchase
    },
    [],
  )

  const totalPurchasedItems = purchases.reduce(
    (sum, p) => sum + p.items.reduce((s, i) => s + i.quantity, 0),
    0,
  )

  return { purchases, addPurchase, totalPurchasedItems }
}
